import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { DashboardCommercialRow, ForecastingRow, OpportuniteRow } from '@/lib/database.types';

export function useDashboardCommercial(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['crm_dashboard', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<DashboardCommercialRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('v_dashboard_commercial')
        .select('*')
        .eq('entreprise_id', entrepriseId);
      if (error) throw error;
      return (data ?? []) as unknown as DashboardCommercialRow[];
    },
  });
}

export function useForecasting(filter: {
  entrepriseId: string | null;
  pipelineId?: string | null;
  commercialId?: string | null;
}) {
  return useQuery({
    queryKey: ['crm_forecasting', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<ForecastingRow[]> => {
      if (!filter.entrepriseId) return [];
      let q = supabase
        .from('v_forecasting_pipelines')
        .select('*')
        .eq('entreprise_id', filter.entrepriseId);
      if (filter.pipelineId) q = q.eq('pipeline_id', filter.pipelineId);
      if (filter.commercialId) q = q.eq('commercial_id', filter.commercialId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ForecastingRow[];
    },
  });
}

export interface OpportuniteUrgente {
  id: string;
  reference: string;
  titre: string;
  montant_estime_dzd: number;
  entreprise_id: string;
  commercial_id: string | null;
  pipeline_id: string;
  etape_id: string;
  etape_nom: string;
  date_creation: string;
  derniere_activite_at: string | null;
  jours_sans_activite: number;
}

export function useOpportunitesUrgentes(entrepriseId: string | null, commercialId?: string | null) {
  return useQuery({
    queryKey: ['crm_opportunites_urgentes', entrepriseId, commercialId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<OpportuniteUrgente[]> => {
      if (!entrepriseId) return [];
      let q = supabase
        .from('v_opportunites_urgentes')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('jours_sans_activite', { ascending: false })
        .limit(50);
      if (commercialId) q = q.eq('commercial_id', commercialId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as OpportuniteUrgente[];
    },
  });
}

export function useRecentWonLost(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['crm_recent_won_lost', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<OpportuniteRow[]> => {
      if (!entrepriseId) return [];
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
      const { data, error } = await supabase
        .from('opportunites')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .in('statut', ['won', 'lost'])
        .gte('date_cloture_reelle', sevenDaysAgo)
        .order('date_cloture_reelle', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as unknown as OpportuniteRow[];
    },
  });
}
